/** The Waiting Room (spec v1.2). Split module, barrel re-exports. */
import { API_URL, call, get, num, patch, post } from "./core";

/**
 * `GET /waiting/:handle` IS GONE (ruling R-0916-3). It answered three keys —
 * handle, state, count — to anybody who could type a URL, which is the whole of
 * ToU 2.2 §5A.5 undone by one endpoint. The room screen no longer reads before
 * it writes: the POST that leaves the pre-knock is the one round trip, and
 * every answer the read used to carry (this handle is yours, it is suppressed,
 * nobody holds it, your lookups are spent) comes back from it.
 */

/**
 * THE FREE CHECK, OVER HTTP (ruling R-0910-2 #2 and #3).
 *
 * `GET /door/:handle/exists` costs nobody anything: Nob's own records, then the
 * miss cache, then X's keyless embed endpoint. It is what every road asks BEFORE
 * anybody is offered the paid one, and it is the only handle question this
 * client is allowed to ask on a keystroke or a page load.
 *
 * `exists` is three-valued on purpose. `null` is X not having answered, and it
 * must never be drawn as "nobody has that handle": telling somebody their friend
 * does not exist on the strength of an outage is the one failure mode worse than
 * saying nothing.
 */
export type HandleExists = {
  handle: string;
  exists: boolean | null;
  /** Nob could answer without asking X — so the waiting room is free to open. */
  known: boolean;
  door: boolean;
};

export async function fetchHandleExists(handle: string): Promise<HandleExists> {
  const bare = handle.replace(/^@/, "").trim();
  const r = await call(() => get(`/api/v1/door/${encodeURIComponent(bare)}/exists`));
  if (!r || !r.res.ok) return { handle: bare, exists: null, known: false, door: false };
  const b = r.body as Record<string, unknown>;
  return {
    handle: typeof b["handle"] === "string" ? b["handle"] : bare,
    exists: typeof b["exists"] === "boolean" ? b["exists"] : null,
    known: b["known"] === true,
    door: b["door"] === true,
  };
}

/**
 * THE INTENT TAP (ruling R-0910-2 #4). A signed-in person pressed "Look them up
 * on X", and that press is what authorises the cent X charges for the one thing
 * the free roads cannot produce: the account's immutable id.
 *
 * The two limit codes are separate because they carry different sentences — a
 * Free account is offered a plan, a paid one is told to come back tomorrow — and
 * a single code would have to guess which, wrongly, for the person who has
 * already paid us.
 */
export type HandleLookupResult =
  | { ok: true; handle: string; door: boolean }
  | {
      ok: false;
      code:
        | "HANDLE_UNRESOLVED"
        | "LOOKUP_LIMIT_FREE"
        | "LOOKUP_LIMIT_PAID"
        | "UNAUTHENTICATED"
        | "UNKNOWN";
    };

export async function lookUpHandleOnX(handle: string): Promise<HandleLookupResult> {
  const bare = handle.replace(/^@/, "").trim();
  const r = await call(() => post(`/api/v1/handle/${encodeURIComponent(bare)}/lookup`, {}));
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.ok) {
    const b = r.body as Record<string, unknown>;
    return {
      ok: true,
      handle: typeof b["handle"] === "string" ? b["handle"] : bare,
      door: b["door"] === true,
    };
  }
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  const code = (r.body as { error?: { code?: string } }).error?.code;
  const known = ["HANDLE_UNRESOLVED", "LOOKUP_LIMIT_FREE", "LOOKUP_LIMIT_PAID"] as const;
  return { ok: false, code: known.find((k) => k === code) ?? "UNKNOWN" };
}

export type AdvocacyAssets = {
  tweet: string;
  tweet_url: string;
  url: string;
  nudge: string;
};

/**
 * NO POSITION AND NO COUNT ON THE WAY BACK (ruling R-0916-3): the 201 confirms
 * the pre-knock, names nothing about anyone else, and hands over the share
 * material. The refusal list grew instead — the read that used to report a
 * suppressed handle, an unresolvable one and a spent lookup allowance is gone,
 * and this POST is where those four answers arrive now.
 */
export type RegisterWaitingResult =
  | { ok: true; assets: AdvocacyAssets }
  | {
      ok: false;
      code:
        | "ALREADY_REGISTERED"
        | "OWN_HANDLE"
        | "REGISTRATION_LIMIT"
        | "REGISTRATION_REFUSED"
        | "DOOR_ALREADY_OPEN"
        | "HANDLE_SUPPRESSED"
        | "HANDLE_UNRESOLVED"
        /** R-0910-2 #4 — the day's lookups are spent. Two codes, two sentences. */
        | "LOOKUP_LIMIT_FREE"
        | "LOOKUP_LIMIT_PAID"
        | "UNAUTHENTICATED"
        | "VALIDATION_ERROR"
        | "UNKNOWN";
      /** ALREADY_REGISTERED carries the line that stands. */
      line: string | null;
    };

function normaliseAssets(raw: unknown): AdvocacyAssets {
  const a = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
  const text = (key: string) => (typeof a[key] === "string" ? (a[key] as string) : "");
  return {
    tweet: text("tweet"),
    tweet_url: text("tweet_url"),
    url: text("url"),
    nudge: text("nudge"),
  };
}

export async function registerWaiting(
  handle: string,
  line: string,
): Promise<RegisterWaitingResult> {
  const r = await call(() =>
    post(`/api/v1/waiting/${encodeURIComponent(handle)}/register`, { line }),
  );
  if (!r) return { ok: false, code: "UNKNOWN", line: null };
  if (r.res.ok) {
    const b = r.body as Record<string, unknown>;
    return { ok: true, assets: normaliseAssets(b["assets"]) };
  }
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED", line: null };
  const err = (r.body as { error?: { code?: string; details?: { line?: unknown } } }).error;
  const known = [
    "ALREADY_REGISTERED",
    "OWN_HANDLE",
    "REGISTRATION_LIMIT",
    "REGISTRATION_REFUSED",
    "DOOR_ALREADY_OPEN",
    "HANDLE_SUPPRESSED",
    "HANDLE_UNRESOLVED",
    "LOOKUP_LIMIT_FREE",
    "LOOKUP_LIMIT_PAID",
    "VALIDATION_ERROR",
  ] as const;
  const code = known.find((k) => k === err?.code) ?? "UNKNOWN";
  return {
    ok: false,
    code,
    line: typeof err?.details?.line === "string" ? err.details.line : null,
  };
}

/**
 * The asking tools, on demand (FR-G6).
 *
 * They used to be handed over exactly once, in the response to a registration,
 * on a screen nobody could get back to. A spot is a CONVERSATION now, so the
 * ask lives in it and can be picked up whenever the registrant is ready to
 * make it — which is what `POST /advocate` was always for: it answers with the
 * assets and marks that this person went to do the asking.
 */
export async function advocateWaiting(handle: string): Promise<AdvocacyAssets | null> {
  const r = await call(() => post(`/api/v1/waiting/${encodeURIComponent(handle)}/advocate`, {}));
  if (!r || !r.res.ok) return null;
  return normaliseAssets((r.body as Record<string, unknown>)["assets"]);
}

export type UpdateWaitingResult =
  | { ok: true; line: string }
  | {
      ok: false;
      code:
        | "REGISTRATION_NOT_FOUND"
        | "REGISTRATION_REFUSED"
        | "HANDLE_SUPPRESSED"
        | "UNAUTHENTICATED"
        | "VALIDATION_ERROR"
        | "UNKNOWN";
    };

/**
 * Rewriting a line that is already held (owner, 2026-08-27).
 *
 * PATCH, and the distinction is the whole of it: a second POST would be a
 * second ARRIVAL — a new spot, a moved counter — and what the registrant is
 * doing is fixing a sentence, not queueing twice. The server leaves the record
 * exactly where it was and re-screens the words.
 */
export async function updateWaitingLine(
  handle: string,
  line: string,
): Promise<UpdateWaitingResult> {
  const r = await call(() =>
    patch(`/api/v1/waiting/${encodeURIComponent(handle)}/register`, { line }),
  );
  if (!r) return { ok: false, code: "UNKNOWN" };
  if (r.res.ok) {
    const b = r.body as Record<string, unknown>;
    return { ok: true, line: typeof b["line"] === "string" ? b["line"] : line };
  }
  if (r.res.status === 401) return { ok: false, code: "UNAUTHENTICATED" };
  const code = (r.body as { error?: { code?: string } }).error?.code;
  const known = [
    "REGISTRATION_NOT_FOUND",
    "REGISTRATION_REFUSED",
    "HANDLE_SUPPRESSED",
    "VALIDATION_ERROR",
  ] as const;
  return { ok: false, code: known.find((k) => k === code) ?? "UNKNOWN" };
}

export async function withdrawWaiting(handle: string): Promise<boolean> {
  const r = await call(() =>
    fetch(`${API_URL}/api/v1/waiting/${encodeURIComponent(handle)}/register`, {
      method: "DELETE",
      credentials: "include",
    }),
  );
  return r?.res.ok ?? false;
}

/**
 * One held spot at the viewer's own door (WR FR-G9, PRD FR-6.3).
 *
 * READ THE FIELD LIST. There is no band, no percentile, no score and no
 * standing, and that is the type doing the work rather than a comment asking
 * for restraint: a held spot earned no judgement, so the shape it arrives in
 * cannot express one. `position` is the queue number they registered at.
 */
export type HeldSpot = {
  id: string;
  line: string;
  position: number;
  handle: string;
  display_name: string;
  created_at: string;
};

/** The held spots at your own door. An empty list for anyone without one. */
export async function fetchHeldSpots(): Promise<HeldSpot[]> {
  const r = await call(() => get("/api/v1/waiting/held"));
  if (!r || !r.res.ok) return [];
  const rows = (r.body as Record<string, unknown>)["held"];
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((raw) => {
    if (!raw || typeof raw !== "object") return [];
    const x = raw as Record<string, unknown>;
    if (typeof x["id"] !== "string") return [];
    const text = (key: string) => (typeof x[key] === "string" ? (x[key] as string) : "");
    return [
      {
        id: x["id"],
        line: text("line"),
        position: num(x["position"]),
        handle: text("handle"),
        display_name: text("display_name"),
        created_at: text("created_at"),
      },
    ];
  });
}

/**
 * One of the viewer's OWN registrations (FR-G3, `GET /waiting/mine`).
 *
 * NOT `HeldSpot`, and the two must never be confused: a held spot is somebody
 * waiting at YOUR handle, this is you waiting at SOMEBODY ELSE'S. The knock
 * dialog needs the second one — it is what makes a row read "Spot reserved"
 * instead of offering to reserve a spot that is already held.
 *
 * AND IT CARRIES NO POSITION (ruling R-0916-3). `HeldSpot` keeps one, because
 * that payload is the OWNER's own console and the owner is the one reader ToU
 * 2.2 §5A.5 allows. This one is the sender's, and "you're #6" tells them five
 * people got there first.
 */
export type MyWaiting = {
  handle: string;
  line: string;
  /**
   * Whether the spot is still held. `withdrawn` rows stay in the list on
   * purpose (owner, F-27): stopping releases the SPOT, not the conversation —
   * the thread reads as stopped and offers to hold the spot again. Converted
   * and purged registrations are the ones that do not arrive here at all.
   */
  state: "active" | "withdrawn";
  /** When they registered. It is what orders the spot among the conversations. */
  created_at: string;
  /**
   * THE DOOR OPENED WHILE THEY WERE WAITING (owner item 3, 2026-08-27).
   *
   * `demand_state === "converted"`, which is the server's record of the one
   * moment this whole system exists to produce. It used to be dropped on the
   * floor here, and dropping it is what made the door opening look like a bug
   * from the registrant's side: the handle left the board, the typed-handle
   * road started answering "this door is live", and the line they wrote went
   * with both. The registration itself never moved — it is still `active` — so
   * the line is still theirs to knock with.
   */
  doorOpen: boolean;
  /**
   * They have already knocked for real. `state === "converted"` on the
   * registration, stamped by W10 when the knock was created out of it, and the
   * reason the offer to knock stops being made rather than standing for ever.
   */
  knocked: boolean;
};

/**
 * Everywhere the viewer is waiting — or stopped. An empty list is a normal
 * answer.
 *
 * ACTIVE AND WITHDRAWN, AND NOTHING ELSE. A withdrawn spot is still a
 * conversation of the viewer's (owner, F-27): the line they wrote is theirs to
 * read back and to hold again, so stopping must not erase the thread from the
 * console. Converted registrations are excluded because they are represented
 * by the knock they became (FR-G9.3) — a pre-knock row beside that knock would
 * be the same conversation twice — and purged ones never arrive at all.
 */
export async function fetchMyWaiting(): Promise<MyWaiting[]> {
  const r = await call(() => get("/api/v1/waiting/mine"));
  if (!r || !r.res.ok) return [];
  const rows = (r.body as Record<string, unknown>)["registrations"];
  if (!Array.isArray(rows)) return [];
  return rows.flatMap((raw) => {
    if (!raw || typeof raw !== "object") return [];
    const x = raw as Record<string, unknown>;
    if (typeof x["handle"] !== "string") return [];
    if (x["state"] !== "active" && x["state"] !== "withdrawn") return [];
    return [
      {
        handle: x["handle"],
        line: typeof x["line"] === "string" ? x["line"] : "",
        state: x["state"],
        created_at: typeof x["created_at"] === "string" ? x["created_at"] : "",
        doorOpen: x["demand_state"] === "converted",
        // Constant false by construction: the filter above never admits a
        // converted registration, which is represented by the knock it became
        // (FR-G9.3). The field survives so the shelf's reading —
        // doorOpen && !knocked — is stated against the type, not a comment.
        knocked: false,
      },
    ];
  });
}

/**
 * The registrations whose door has since opened and that have not been knocked
 * on yet — the conversion moment, as a list.
 *
 * A FUNCTION RATHER THAN A SECOND ENDPOINT, because it is a reading of the
 * payload `/waiting/mine` already sends and a second endpoint would be a second
 * place for the two conditions to disagree. Both conditions are needed and they
 * are different facts: the DOOR converted (the demand record) and the SPOT has
 * not (the registration). One without the other is either a queue that is still
 * a queue or a knock that has already been sent.
 *
 * AND THE SPOT MUST STILL BE HELD. A withdrawn registration rides the list as
 * a stopped conversation now, and stopping was this person saying they no
 * longer want the door — the opening is not news addressed to them, and a
 * "Knock for real" shelf over a spot they let go of would be the product
 * arguing with their own decision.
 */
export function openedWhileWaiting(rows: MyWaiting[]): MyWaiting[] {
  return rows.filter((r) => r.doorOpen && !r.knocked && r.state === "active");
}

export type WaitingPreview = {
  advisory: true;
  waiting: number;
  pending: number;
  shown: number;
  registrations: { line: string; position: number; verified: boolean; tier: string }[];
};

/** The owner's conversion preview — null when no demand was waiting. */
export async function fetchWaitingPreview(): Promise<WaitingPreview | null> {
  const r = await call(() => get("/api/v1/waiting/preview/own"));
  if (!r || !r.res.ok) return null;
  const b = r.body as Record<string, unknown>;
  const rows = Array.isArray(b["registrations"]) ? b["registrations"] : [];
  return {
    advisory: true,
    waiting: num(b["waiting"]),
    pending: num(b["pending"]),
    shown: num(b["shown"]),
    registrations: rows.flatMap((raw) => {
      if (!raw || typeof raw !== "object") return [];
      const x = raw as Record<string, unknown>;
      return [
        {
          line: typeof x["line"] === "string" ? x["line"] : "",
          position: num(x["position"]),
          verified: x["verified"] === true,
          tier: typeof x["tier"] === "string" ? x["tier"] : "reasonable",
        },
      ];
    }),
  };
}
